import { useEffect, useRef, useState } from 'react';
import {
  RoomEvent,
  Track,
  type Room,
  type RemoteTrack,
  type RemoteTrackPublication,
  type RemoteParticipant,
  type RemoteAudioTrack,
} from 'livekit-client';

interface AudioEntry {
  track: RemoteAudioTrack;
  source: Track.Source;
}

/**
 * Per-track audio element. Uses a stable `useEffect` to call
 * `track.attach(el)` exactly once per real (track, element) lifecycle and
 * `track.detach(el)` exactly once on unmount. Inline ref callbacks (whose
 * identity changes every render) would trigger detach/attach cycles on
 * every parent re-render, defeating the "no interruption" guarantee.
 */
function TrackAudioElement({
  track,
  source,
  muted = false,
}: {
  track: RemoteAudioTrack;
  source: Track.Source;
  muted?: boolean;
}) {
  const ref = useRef<HTMLAudioElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    try {
      track.attach(el);
      console.info('[Call/audio] sink attach', { sid: track.sid, source });
    } catch (err) {
      console.error('[Call/audio] sink attach failed', { sid: track.sid, source, err });
    }
    return () => {
      try {
        track.detach(el);
        console.info('[Call/audio] sink detach', { sid: track.sid, source });
      } catch (err) {
        console.error('[Call/audio] sink detach failed', { sid: track.sid, source, err });
      }
    };
  }, [track, source]);
  // Apply listener-side mute (does NOT affect publisher or other listeners).
  // Keyed only on `muted` so attach/detach above is never re-run by toggling.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.muted = muted;
  }, [muted]);
  return <audio ref={ref} autoPlay playsInline />;
}

/**
 * Single owner of remote-audio `<audio>` elements for the active call.
 *
 * Mounted ONCE per call in `InCallWindow` OUTSIDE the Document
 * Picture-in-Picture portal subtree, so the audio elements always live in the
 * original document that received the user-gesture autoplay grant at
 * call-accept time. The visual layer (ParticipantTile / ScreenShareStage)
 * never touches audio tracks, so it can be portaled in/out freely without
 * causing detach/re-attach churn or losing the autoplay context.
 *
 * Subscribes directly to `Room` lifecycle events and maintains a local map
 * keyed by `track.sid` of every subscribed remote `Microphone` /
 * `ScreenShareAudio` track. Each map entry renders one `<audio
 * autoPlay playsInline>`; the ref-callback wires `track.attach(el)` on mount
 * and `track.detach(el)` on unmount, which is the same path LiveKit's
 * `switchActiveDevice('audiooutput', ...)` consults — so the speaker picker
 * continues to work transparently.
 */
export function GlobalCallAudioSink({
  room,
  screenShareAudioMuted = false,
}: {
  room: Room;
  /**
   * Listener-side mute for ALL audio elements whose source is
   * `Track.Source.ScreenShareAudio`. Toggled by the ScreenShareStage overlay
   * button so listeners can silence a presenter's tab/system audio without
   * affecting the presenter's microphone. Resets to false whenever the share
   * owner changes (handled by the caller).
   */
  screenShareAudioMuted?: boolean;
}) {
  const [entries, setEntries] = useState<Map<string, AudioEntry>>(() => new Map());

  useEffect(() => {
    const isAudibleSource = (s: Track.Source) =>
      s === Track.Source.Microphone || s === Track.Source.ScreenShareAudio;

    const upsert = (track: RemoteTrack, pub: RemoteTrackPublication) => {
      if (track.kind !== Track.Kind.Audio) return;
      if (!isAudibleSource(pub.source)) return;
      const sid = pub.trackSid;
      if (!sid) return;
      setEntries((prev) => {
        if (prev.has(sid)) return prev;
        const next = new Map(prev);
        next.set(sid, { track: track as RemoteAudioTrack, source: pub.source });
        return next;
      });
    };

    const remove = (_track: RemoteTrack | undefined, pub: RemoteTrackPublication) => {
      const sid = pub.trackSid;
      if (!sid) return;
      setEntries((prev) => {
        if (!prev.has(sid)) return prev;
        const next = new Map(prev);
        next.delete(sid);
        return next;
      });
    };

    const handleParticipantDisconnected = (p: RemoteParticipant) => {
      setEntries((prev) => {
        let mutated = false;
        const next = new Map(prev);
        p.trackPublications.forEach((pub) => {
          const sid = pub.trackSid;
          if (sid && next.has(sid)) {
            next.delete(sid);
            mutated = true;
          }
        });
        return mutated ? next : prev;
      });
    };

    // Initial sweep: pick up tracks that were already subscribed before mount.
    room.remoteParticipants.forEach((p) => {
      p.trackPublications.forEach((pub) => {
        if (pub.track && pub.isSubscribed) {
          upsert(pub.track, pub as RemoteTrackPublication);
        }
      });
    });

    room.on(RoomEvent.TrackSubscribed, upsert);
    room.on(RoomEvent.TrackUnsubscribed, remove);
    room.on(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected);
    return () => {
      room.off(RoomEvent.TrackSubscribed, upsert);
      room.off(RoomEvent.TrackUnsubscribed, remove);
      room.off(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected);
    };
  }, [room]);

  return (
    <div className="hidden" aria-hidden>
      {Array.from(entries.entries()).map(([sid, entry]) => (
        <TrackAudioElement
          key={sid}
          track={entry.track}
          source={entry.source}
          muted={entry.source === Track.Source.ScreenShareAudio && screenShareAudioMuted}
        />
      ))}
    </div>
  );
}
